<template>
  <section class="filterbar mb-filterbar" aria-label="Filter">
    <!-- UX (mobil): Die Leiste schrumpft auf einen Button + Chips, die Filter öffnen als Bottom-Sheet. -->
    <div class="mb-filterbar__mobile">
      <button type="button" class="mb-sheet-toggle" :aria-expanded="sheetOpen ? 'true' : 'false'" @click="sheetOpen = true">
        <i class="fas fa-sliders" aria-hidden="true" />
        Filter
        <span v-if="activeChips.length" class="mb-sheet-toggle__count">{{ activeChips.length }}</span>
      </button>
      <span class="mb-filterbar__period-inline">{{ activePeriodLabel }}</span>
    </div>

    <div v-if="sheetOpen" class="mb-sheet-back" @click="sheetOpen = false" />
    <div class="mb-filterbar__panel" :class="{ 'is-open': sheetOpen }">
      <div class="mb-sheet-head">
        <strong>Filter</strong>
        <button type="button" class="mb-sheet-close" aria-label="Filter schließen" @click="sheetOpen = false">×</button>
      </div>

      <!-- Ebene 1 – globale Filter, auf allen 6 Seiten identisch. -->
      <div class="frow mb-filterbar__global">
        <FilterPair v-if="markeOptions.length > 2" label="Marke">
          <SingleDropdown
            label="Marke"
            :options="markeOptions"
            :model-value="String(filters.marke_id || '')"
            @update:model-value="(value) => setFilter({ marke_id: value, product_id: '', project_id: '', campaign_id: '', measure_id: '' })"
          />
        </FilterPair>
        <!-- UX: Das Feedback nennt diesen Filter "Marke". Gibt es nur eine Marke,
             ist das Produkt die Markenauswahl des Nutzers und heißt entsprechend. -->
        <FilterPair :label="markeOptions.length > 2 ? 'Produkt' : 'Marke'">
          <SingleDropdown
            :label="markeOptions.length > 2 ? 'Produkt' : 'Marke'"
            :options="productOptions"
            :model-value="String(filters.product_id || '')"
            @update:model-value="onProduct"
          />
        </FilterPair>
        <FilterPair label="Projekt">
          <SingleDropdown
            label="Projekt"
            :options="projectOptions"
            :model-value="String(filters.project_id || '')"
            @update:model-value="(value) => setFilter({ project_id: value, campaign_id: '', measure_id: '' })"
          />
        </FilterPair>
        <FilterPair label="Kampagne">
          <SingleDropdown
            label="Kampagne"
            :options="campaignOptions"
            :model-value="String(filters.campaign_id || '')"
            @update:model-value="(value) => setFilter({ campaign_id: value, measure_id: '' })"
          />
        </FilterPair>
        <FilterPair label="Zeitraum">
          <PeriodPicker />
        </FilterPair>
        <button
          type="button"
          class="mb-more-toggle"
          :class="{ on: moreOpen || moreActiveCount }"
          :aria-expanded="moreOpen ? 'true' : 'false'"
          @click="moreOpen = !moreOpen"
        >
          + Weitere Filter<span v-if="moreActiveCount" class="mb-more-toggle__count">{{ moreActiveCount }}</span>
        </button>
        <!-- Desktop: Icon oben rechts in der Ecke, Text erscheint beim Überfahren. -->
        <button type="button" class="mb-reset" aria-label="Filter zurücksetzen" @click="resetFilters">
          <i class="fas fa-rotate-left" aria-hidden="true" /><span class="mb-reset__text">Zurücksetzen</span>
        </button>
      </div>

      <!-- UX: Selten genutzte Filter hinter "Weitere Filter". Kanal/Schaltungstyp nur dort, wo sie sinnvoll sind. -->
      <div v-show="moreOpen" class="frow mb-filterbar__more">
        <FilterPair label="Maßnahme">
          <SingleDropdown
            label="Maßnahme"
            :options="measureOptions"
            :model-value="String(filters.measure_id || '')"
            @update:model-value="(value) => setFilter({ measure_id: value })"
          />
        </FilterPair>
        <FilterPair label="Status">
          <SingleDropdown
            label="Status"
            :options="statusOptions"
            :model-value="String(filters.status || '')"
            @update:model-value="(value) => setFilter({ status: value })"
          />
        </FilterPair>
        <FilterPair v-if="visible('channel')" label="Kanal">
          <SingleDropdown
            label="Kanal"
            :options="channelOptions"
            :model-value="String(filters.channel || '')"
            @update:model-value="(value) => setFilter({ channel: value, measure_id: '' })"
          />
        </FilterPair>
        <FilterPair v-if="visible('placement_type')" label="Schaltungstyp">
          <SingleDropdown
            label="Schaltungstyp"
            :options="placementOptions"
            :model-value="String(filters.placement_type || '')"
            @update:model-value="(value) => setFilter({ placement_type: value })"
          />
        </FilterPair>
      </div>

      <!-- Ebene 2 – kanalspezifische Filter als abgesetzte Fläche (jr-panel). -->
      <div v-show="pageFilters.render" class="jr-panel mb-page-filters">
        <div class="jr-panel__texts">
          <span class="jr-panel__title">Filter für {{ channelLabel }}</span>
          <span class="jr-panel__hint">Gilt nur für diese Seite.</span>
        </div>
        <span class="jr-panel__divider" aria-hidden="true" />
        <div class="frow">
          <PageFiltersOutlet />
        </div>
      </div>

      <div class="mb-sheet-foot">
        <button type="button" class="jr-button" @click="sheetOpen = false">Ergebnisse anzeigen</button>
      </div>
    </div>

    <!-- UX: Aktive Filter als entfernbare Chips – auch solche, die auf dieser Seite ausgeblendet sind. -->
    <div v-if="activeChips.length" class="frow mb-active">
      <span class="mb-active__label">Aktiv:</span>
      <button
        v-for="chip in activeChips"
        :key="chip.key"
        type="button"
        class="mb-active__chip"
        :aria-label="`Filter ${chip.label} entfernen`"
        @click="setFilter(chip.clear)"
      >
        <span class="mb-active__name">{{ chip.label }}:</span> {{ chip.value }} <span aria-hidden="true">×</span>
      </button>
    </div>
  </section>
</template>

<script setup>
import { computed, defineComponent, h, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import SingleDropdown from '@/features/dashboard/components/SingleDropdown.vue';
import { isFilterVisible, useMarketingFilters } from '../composables/useMarketingFilters';
import PageFiltersOutlet from './PageFiltersOutlet.vue';
import PeriodPicker from './PeriodPicker.vue';

const FilterPair = defineComponent({
  name: 'FilterPair',
  props: { label: { type: String, required: true } },
  setup(props, { slots }) {
    return () => h('div', { class: 'fpair' }, [
      h('span', { class: 'flabel' }, props.label),
      slots.default?.()
    ]);
  }
});

const props = defineProps({
  options: { type: Object, default: () => ({}) }
});

const route = useRoute();
const { filters, setFilter, resetFilters, activePeriodLabel, pageFilters } = useMarketingFilters();

const moreOpen = ref(false);
const sheetOpen = ref(false);

const CHANNEL_LABELS = {
  'marketing-digital': 'Digitale Kampagnen',
  'marketing-social': 'Social Media',
  'marketing-email': 'E-Mail',
  'marketing-traffic': 'Website-Traffic',
  'marketing-print': 'Print'
};
const channelLabel = computed(() => CHANNEL_LABELS[route.name] || 'diese Seite');

function visible(key) {
  return isFilterVisible(route.name, key);
}

function mapOptions(items, valueKey, labelKey, allLabel) {
  const mapped = (items || []).map((item) => ({
    value: String(item[valueKey]),
    label: item[labelKey]
  }));
  return allLabel ? [{ value: '', label: allLabel }, ...mapped] : mapped;
}

const markeOptions = computed(() => mapOptions(props.options.marken, 'id', 'name', 'Alle Marken'));
const productOptions = computed(() => mapOptions(props.options.products, 'id', 'name'));
const projectOptions = computed(() => mapOptions(props.options.projects, 'id', 'name', 'Alle Projekte'));
const campaignOptions = computed(() => mapOptions(props.options.campaigns, 'id', 'name', 'Alle Kampagnen'));
const measureOptions = computed(() => mapOptions(props.options.measures, 'id', 'name', 'Alle Maßnahmen'));
const channelOptions = computed(() => mapOptions(props.options.channels, 'code', 'name', 'Alle Kanäle'));
const placementOptions = computed(() => mapOptions(props.options.placement_types, 'code', 'name', 'Alle Schaltungstypen'));
const statusOptions = computed(() => mapOptions(props.options.measure_statuses, 'code', 'name', 'Alle Status'));

function optionLabel(options, value) {
  const match = options.find((option) => String(option.value) === String(value));
  return match ? match.label : value;
}

// Gleiche Kaskade wie in den Dropdowns: ein übergeordneter Filter leert die untergeordneten.
const CHIP_DEFS = [
  { key: 'marke_id', label: 'Marke', options: markeOptions, clear: { marke_id: '', project_id: '', campaign_id: '', measure_id: '' } },
  { key: 'project_id', label: 'Projekt', options: projectOptions, clear: { project_id: '', campaign_id: '', measure_id: '' } },
  { key: 'campaign_id', label: 'Kampagne', options: campaignOptions, clear: { campaign_id: '', measure_id: '' } },
  { key: 'measure_id', label: 'Maßnahme', options: measureOptions, clear: { measure_id: '' }, more: true },
  { key: 'status', label: 'Status', options: statusOptions, clear: { status: '' }, more: true },
  { key: 'channel', label: 'Kanal', options: channelOptions, clear: { channel: '', measure_id: '' }, more: true },
  { key: 'placement_type', label: 'Schaltungstyp', options: placementOptions, clear: { placement_type: '' }, more: true }
];

const activeChips = computed(() => CHIP_DEFS
  .filter((def) => filters[def.key])
  .map((def) => ({
    key: def.key,
    label: def.label,
    value: optionLabel(def.options.value, filters[def.key]),
    clear: def.clear
  })));

const moreActiveCount = computed(() => CHIP_DEFS
  .filter((def) => def.more && filters[def.key] && visible(def.key)).length);

function onProduct(value) {
  setFilter({ product_id: value, project_id: '', campaign_id: '', measure_id: '' });
}

// Beim Seitenwechsel das Bottom-Sheet schließen.
watch(() => route.name, () => {
  sheetOpen.value = false;
});
</script>
